import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import moment from 'moment-timezone';

import {
  GET_NEXT_CUTOFF_TIME_FOR_PROGRAM_SUBSCRIPTION,
  LIST_UNLOCKED_AVAILABLE_DELIVERY_DAYS,
} from 'core/api/apollo/queries';

// Constants
const DATE_FORMAT = 'dddd, MMMM Do';
const TIME_FORMAT = 'h:mm A';
const PACIFIC_TIMEZONE = 'America/Los_Angeles';

// Utility functions
const formatCutoffDateTime = (dateTime) => {
  if (!dateTime) return { date: '', time: '' };
  // Convert to Pacific Time (PST/PDT)
  const pacificTime = moment(dateTime).tz(PACIFIC_TIMEZONE);

  return {
    date: pacificTime.format(DATE_FORMAT),
    time: pacificTime.format(TIME_FORMAT),
  };
};

const filterAvailableDeliveryDays = (deliveryDays) => {
  return deliveryDays.filter((day) => !day.locked && day.menuPublished);
};

// Custom hooks
export const useDeliveryDays = () => {
  const { loading, data = {}, error } = useQuery(LIST_UNLOCKED_AVAILABLE_DELIVERY_DAYS);
  const { listUnlockedAvailableDeliveryDays = [] } = data;

  const availableDeliveryDays = useMemo(() => {
    return filterAvailableDeliveryDays(listUnlockedAvailableDeliveryDays);
  }, [listUnlockedAvailableDeliveryDays]);

  return {
    loading,
    error,
    deliveryDays: availableDeliveryDays,
    firstPeriod: availableDeliveryDays[0] || null,
  };
};

export const useCutoffTime = (deliveryDayId) => {
  const { loading, data = {}, error } = useQuery(GET_NEXT_CUTOFF_TIME_FOR_PROGRAM_SUBSCRIPTION, {
    variables: { deliveryDayId },
    skip: !deliveryDayId,
  });

  const cutoffDateTime = data.getNextCutoffTimeForProgramSubscription;
  const formattedDateTime = useMemo(() => {
    return formatCutoffDateTime(cutoffDateTime);
  }, [cutoffDateTime]);

  return {
    loading,
    error,
    cutoffDateTime: moment(cutoffDateTime).tz(PACIFIC_TIMEZONE).format('YYYY-MM-DD[T]HH:mm:ss'),
    formattedDateTime,
  };
};
